/**
 * Builds a linear step list (questions + PHQ-9→GAD-7 milestone) from FHIR Questionnaires.
 * @agentic members
 */

import type {
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireItemAnswerOption,
} from "@medplum/fhirtypes";
import {
  QUESTIONNAIRE_URLS,
  STANDARD_QUESTIONNAIRES,
} from "@benchmark/core/constants";

/**
 * One step in the patient assessment flow.
 * @agentic members
 */
export type LinearStep =
  | { kind: "milestone"; milestoneId: "phq9-to-gad7" }
  | {
      kind: "question";
      questionnaireUrl: string;
      sectionLabel: string;
      instructions: string | undefined;
      item: QuestionnaireItem;
      questionOrdinal: number;
      totalQuestions: number;
    };

/**
 * A single answer value on a survey response item. Mirrors the subset of
 * FHIR `QuestionnaireResponse.item.answer` the survey flow produces.
 * @agentic members
 */
export type SurveyResponseAnswer = {
  valueInteger?: number;
  valueString?: string;
  valueCoding?: { code: string; display: string };
};

/**
 * A single survey response item (flattened, one answer per question).
 * @agentic members
 */
export type SurveyResponseItem = {
  linkId: string;
  text?: string;
  answer: SurveyResponseAnswer[];
};

/**
 * FHIR extension URLs that carry an answer option's numeric (Likert) weight.
 * @agentic members
 */
const ANSWER_WEIGHT_EXTENSION_URLS = new Set([
  "http://hl7.org/fhir/StructureDefinition/itemWeight",
  "http://hl7.org/fhir/StructureDefinition/ordinalValue",
]);

/**
 * Resolve the numeric weight for a selected answer option: prefer an explicit
 * weight extension, then fall back to a code that encodes the weight directly
 * (e.g. the local "0"–"3" Likert codes served by the fallback questionnaire).
 * @agentic members
 */
const resolveAnswerWeight = (
  option: QuestionnaireItemAnswerOption | undefined,
  selectedCode: string,
): number | undefined => {
  for (const ext of option?.extension ?? []) {
    if (ANSWER_WEIGHT_EXTENSION_URLS.has(ext.url ?? "")) {
      if (typeof ext.valueDecimal === "number") return ext.valueDecimal;
      if (typeof ext.valueInteger === "number") return ext.valueInteger;
    }
  }
  return /^-?\d+$/.test(selectedCode) ? Number(selectedCode) : undefined;
};

/**
 * Build a FHIR-ready response item for one question. Scorable choice answers
 * carry both `valueInteger` (the numeric weight, matching clinician-authored
 * responses) and `valueCoding` (code + display) so every scoring path resolves
 * a value. Unmatched/free-text answers fall back to `valueString`.
 * @agentic members
 */
export const buildSurveyResponseItem = (
  item: QuestionnaireItem,
  selectedCode: string | undefined,
): SurveyResponseItem => {
  const linkId = item.linkId ?? "";
  if (!selectedCode) {
    return { linkId, text: item.text, answer: [] };
  }

  const matchedOption = item.answerOption?.find(
    (opt) => opt.valueCoding?.code === selectedCode,
  );
  const coding = matchedOption?.valueCoding;
  if (!coding?.code) {
    return { linkId, text: item.text, answer: [{ valueString: selectedCode }] };
  }

  const weight = resolveAnswerWeight(matchedOption, selectedCode);
  return {
    linkId,
    text: item.text,
    answer: [
      {
        ...(weight !== undefined ? { valueInteger: weight } : {}),
        valueCoding: { code: coding.code, display: coding.display ?? "" },
      },
    ],
  };
};

/**
 * Flatten nested group items into answerable choice items.
 * @agentic members
 */
export const flattenQuestionnaireItems = (
  items: QuestionnaireItem[] | undefined,
): QuestionnaireItem[] => {
  const out: QuestionnaireItem[] = [];
  for (const it of items ?? []) {
    if (it.type === "group" && it.item && it.item.length > 0) {
      out.push(...flattenQuestionnaireItems(it.item));
    } else if (
      it.type === "choice" ||
      (it.answerOption && it.answerOption.length > 0)
    ) {
      out.push(it);
    }
  }
  return out;
};

type StandardQuestionnaireConfig =
  (typeof STANDARD_QUESTIONNAIRES)[keyof typeof STANDARD_QUESTIONNAIRES];

const configByUrl = new Map<string, StandardQuestionnaireConfig>(
  (Object.values(STANDARD_QUESTIONNAIRES) as StandardQuestionnaireConfig[]).map(
    (cfg) => [cfg.url, cfg],
  ),
);

/**
 * Short section label for header (e.g. Depression / Anxiety).
 * @agentic members
 */
export const getSectionLabelForUrl = (questionnaireUrl: string): string => {
  if (questionnaireUrl === QUESTIONNAIRE_URLS.PHQ9) {
    return "Depression";
  }
  if (questionnaireUrl === QUESTIONNAIRE_URLS.GAD7) {
    return "Anxiety";
  }
  const cfg = configByUrl.get(questionnaireUrl);
  return cfg?.title ?? "Survey";
};

/**
 * Instruction stem (e.g. PHQ-9 timeframe) from standard questionnaire config.
 * @agentic members
 */
export const getInstructionsForUrl = (
  questionnaireUrl: string,
): string | undefined => configByUrl.get(questionnaireUrl)?.instructions;

/**
 * Total answerable items across questionnaires (for welcome copy).
 * @agentic members
 */
export const countTotalQuestions = (questionnaires: Questionnaire[]): number =>
  questionnaires.reduce(
    (sum, q) => sum + flattenQuestionnaireItems(q.item).length,
    0,
  );

/**
 * Build ordered steps: optional milestone between PHQ-9 and GAD-7, then all questions.
 * @agentic members
 */
export const buildLinearSteps = (
  questionnaires: Questionnaire[],
): LinearStep[] => {
  const raw: LinearStep[] = [];

  for (let i = 0; i < questionnaires.length; i++) {
    const q = questionnaires[i];
    const prev = questionnaires[i - 1];
    const url = q.url ?? "";
    const prevUrl = prev?.url ?? "";

    if (
      i > 0 &&
      prevUrl === QUESTIONNAIRE_URLS.PHQ9 &&
      url === QUESTIONNAIRE_URLS.GAD7
    ) {
      raw.push({ kind: "milestone", milestoneId: "phq9-to-gad7" });
    }

    const flat = flattenQuestionnaireItems(q.item);
    const sectionLabel = getSectionLabelForUrl(url);
    const instructions = getInstructionsForUrl(url);

    for (const item of flat) {
      raw.push({
        kind: "question",
        questionnaireUrl: url,
        sectionLabel,
        instructions,
        item,
        questionOrdinal: 0,
        totalQuestions: 0,
      });
    }
  }

  const totalQuestions = raw.filter((s) => s.kind === "question").length;
  let ord = 0;

  return raw.map((step) => {
    if (step.kind !== "question") {
      return step;
    }
    ord += 1;
    return {
      ...step,
      questionOrdinal: ord,
      totalQuestions,
    };
  });
};
